export type WebGpuCapability = { supported: true } | { supported: false; reason: string };

/**
 * Detect WebGPU support without requesting an adapter.
 * Browsers expose `navigator.gpu` even before user-gesture; that's enough
 * to decide which transformers.js backend to ask for.
 */
export const probeWebGpu = (): WebGpuCapability => {
  if (typeof navigator === 'undefined' || !('gpu' in navigator)) {
    return { supported: false, reason: 'navigator.gpu missing' };
  }
  return { supported: true };
};

/**
 * Async variant — actually requests an adapter. Slower; only call when you
 * really need to know that GPU init will work.
 */
export const verifyWebGpuAdapter = async (): Promise<WebGpuCapability> => {
  const cap = probeWebGpu();
  if (!cap.supported) return cap;
  try {
    const navWithGpu = navigator as Navigator & {
      gpu?: { requestAdapter: () => Promise<unknown> };
    };
    const adapter = await navWithGpu.gpu?.requestAdapter();
    if (!adapter) return { supported: false, reason: 'no adapter available' };
    return { supported: true };
  } catch (e) {
    return {
      supported: false,
      reason: e instanceof Error ? e.message : 'adapter request threw',
    };
  }
};
